import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import moment from 'moment';

import Panel from 'components/Panel';
import Break from 'components/Break';
import Button, {
  BlueButtonWithWriteAccess as CancelButton
} from 'components/Button';
import SectionHeader from 'components/Panels/SectionHeader';
import ControlFieldsContainer from 'components/ControlFieldsContainer';

import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelButtonRow,
  FullPanel,
  PanelContent
} from 'components/PanelComponents';

import { openErrorModal } from 'reducers/modal/actions';
import {
  setSidePanel,
  closeSidePanel,
  revertSidePanel
} from 'reducers/sidePanel/actions';
import {
  API_FIELD_MAPPINGS,
  RESOURCE_TYPES,
  SIDEPANEL_STATES
} from 'utils/appConstants';
import { durationFormatter, isExternallyManaged } from 'utils/format';
import { determineBack } from 'utils/determineBack';
import {
  POLL_CONTROLS_AND_RESPONSES,
  POLL_CURVES,
  POLL_INDENODES,
  POLL_TOPONODES,
  subscribe,
  unsubscribe
} from 'reducers/polling/actions';
import { DEVICE_CATEGORIES } from 'utils/appConstants';
import { TooltipRow } from 'components/Tooltip';
import { calcSfdiFromLfdi } from 'utils/calcSfdiFromLfdi';
import { DATE_FORMAT } from 'utils/appConstants';
import Table from 'components/Table';
import externalLink from 'assets/external-link-square-alt.svg';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const deviceColumns = [
  {
    headerName: 'ID',
    field: 'deviceId'
  },
  {
    headerName: 'LFDI',
    field: 'deviceLfdi'
  },
  {
    headerName: 'Timestamp',
    field: 'timestamp',
    cellRenderer: ({ value }) => moment.unix(value).format(DATE_FORMAT)
  }
];
const AssociatedNodeRow = styled(PanelRowDisplay)`
  display: block;
`;
const ExternalIcon = styled.img`
  height: 15px;
  width: 15px;
  margin-left: 10px;
  display: inline;
  vertical-align: middle;
  cursor: pointer;
  &:hover {
    background-color: ${({ showDropdown }) =>
      showDropdown ? 'rgba(7, 121, 175, 0.2)' : 'rgba(7, 121, 175, 0.05)'};
  }
`;

const WhiteButton = styled(Button)`
  background: #ffffff;
  border: 2px solid #0779af;
  p {
    font-size: 12px;
    color: #0779af;
  }
`;

const ButtonRow = styled.div`
  display: flex;
`;
const subName = 'ControlPanel';
const disabledRemoteMessage = 'Remote controls may not be modified';
const ControlPanel = ({ isActive }) => {
  const id = useSelector(state => state.sidePanel.id);
  const controls = useSelector(state => state.control.controls);
  const deletedControls = useSelector(state => state.control.deletedControls);
  const devices = useSelector(state => state.device.devices);
  const sidePanel = useSelector(state => state.sidePanel);
  const curves = useSelector(state => state.curve.curves);
  const indenodes = useSelector(state => state.indeNode.indeNodes);

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [newControl, setNewControl] = useState({
    control: {
      controlTypes: {}
    },
    responses: {
      devicesReceived: [],
      devicesStarted: [],
      devicesCompleted: [],
      devicesOptOut: [],
      devicesOptIn: [],
      devicesCancelled: [],
      devicesSuperseded: [],
      devicesPartiallyCompleted: [],
      devicesAborted: []
    },
    endTime: null,
    showControlCancelButton: false,
    statusType: null,
    isLoading: true
  });

  const compileControlData = useCallback(() => {
    let deletedFallback = deletedControls || [];
    let selectedControls = controls
      .concat(deletedFallback)
      .filter(singleControl => singleControl.controlId === id);

    if (selectedControls.length === 0) {
      dispatch(openErrorModal('No active control found with id: ' + id));
      dispatch(closeSidePanel());
      return null;
    }

    let [node] = selectedControls;

    let responsesWithDevId = node.responses.map(response => {
      let devResponded = devices.filter(device => {
        return device.lfdi === response.deviceLfdi;
      });

      if (devResponded.length > 0) {
        response.deviceId = devResponded[0].deviceId;
      }

      return response;
    });

    const devicesReceived = responsesWithDevId.filter(
      response => response.status === 1
    );
    const devicesStarted = responsesWithDevId.filter(
      response => response.status === 2
    );
    const devicesCompleted = responsesWithDevId.filter(
      response => response.status === 3 || response.status === 10
    );
    const devicesOptOut = responsesWithDevId.filter(
      response => response.status === 4
    );
    const devicesOptIn = responsesWithDevId.filter(
      response => response.status === 5
    );
    const devicesCancelled = responsesWithDevId.filter(
      response => response.status === 6
    );
    const devicesSuperseded = responsesWithDevId.filter(
      response => response.status === 7
    );
    const devicesPartiallyCompleted = responsesWithDevId.filter(
      response => response.status === 8 || response.status === 9
    );
    const devicesAborted = responsesWithDevId.filter(
      response => response.status === 13 || response.status === 14
    );

    const endTime = node.start + node.duration;

    // if Control Status is Scheduled(0) or Active(1) Show Cancel Button
    //  mappings found here: API_FIELD_MAPPINGS.CONTROL_STATUS_DISPLAYS.type
    // And only show cancel button if the control is not already expired.
    const showControlCancelButton =
      [0, 1].includes(node.status.value) && moment().unix() < endTime
        ? true
        : false;
    const statusType =
      API_FIELD_MAPPINGS.CONTROL_STATUS_DISPLAYS.type[node.status.value]
        .displayText;

    const newControl = {
      control: node,
      responses: {
        devicesReceived,
        devicesStarted,
        devicesCompleted,
        devicesOptOut,
        devicesOptIn,
        devicesCancelled,
        devicesSuperseded,
        devicesPartiallyCompleted,
        devicesAborted
      },
      endTime,
      showControlCancelButton,
      statusType
    };
    setNewControl(newControl);
  }, [controls, deletedControls, devices, id, dispatch]);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: subName,
          pollTypes: [
            {
              type: POLL_CONTROLS_AND_RESPONSES
            },
            {
              type: POLL_CURVES
            },
            {
              type: POLL_INDENODES
            },
            {
              type: POLL_TOPONODES
            }
          ],
          pollRate
        })
      );
      compileControlData();
    } else {
      dispatch(unsubscribe(subName));
    }
    return () => {
      if (isActive) {
        dispatch(unsubscribe(subName));
      }
    };
  }, [dispatch, compileControlData, sidePanel.id, isActive]);

  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [isLoading, newControl]);

  const onDeviceSelected = useCallback(
    row => {
      const { deviceLfdi } = row.data;
      const sfdi = parseInt(calcSfdiFromLfdi(deviceLfdi));
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.DEVICE,
          state: SIDEPANEL_STATES.READ,
          sfdi,
          keepBreadcrumbs: true
        })
      );
    },
    [dispatch]
  );

  const getNodeType = nodeId => {
    const indenode = indenodes.find(indeNode => indeNode.indeNodeId === nodeId);
    if (indenode) {
      return RESOURCE_TYPES.INDENODE;
    } else {
      return RESOURCE_TYPES.TOPONODE;
    }
  };

  const onAssociatedNode = () => {
    const { nodeId } = control;
    const foundNode = getNodeType(nodeId);
    return dispatch(
      setSidePanel({
        entity: foundNode,
        state: SIDEPANEL_STATES.READ,
        keepBreadcrumbs: true,
        id: control.nodeId
      })
    );
  };

  const {
    control,
    responses: {
      devicesReceived,
      devicesStarted,
      devicesCompleted,
      devicesOptOut,
      devicesOptIn,
      devicesCancelled,
      devicesSuperseded,
      devicesPartiallyCompleted,
      devicesAborted
    },
    endTime,
    showControlCancelButton,
    statusType
  } = newControl;

  if (!isActive || (isActive && isLoading) || control === undefined) {
    return null;
  } else {
    const { controlTypes, localControl: localControlValue } =
      newControl.control;

    const isRemoteResource =
      externallyManagedEdevs && isExternallyManaged(localControlValue, true);

    const deviceCategories = () => {
      const parsedDcHex = parseInt(control.deviceCategory, 16);
      let deviceCategoryArr = [];
      for (let dcInt = parsedDcHex; dcInt > 0; ) {
        let largestPower = Math.log2(dcInt);
        let foundIndex = Math.floor(largestPower);
        deviceCategoryArr.push(DEVICE_CATEGORIES[foundIndex]);
        dcInt = dcInt - Math.pow(2, foundIndex);
      }
      return (
        <div>
          {deviceCategoryArr.map((deviceCategory, i) => {
            return <div key={i}>{deviceCategory}</div>;
          })}
        </div>
      );
    };

    return (
      <FullPanel>
        <Panel title="Control Info" primaryHeader />
        <PanelContent>
          <SubPanel>
            <SubPanelRow>
              <PanelRowDescriptor>Description</PanelRowDescriptor>
              <PanelRowDisplay>{control.description}</PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Status</PanelRowDescriptor>
              <PanelRowDisplay>{statusType}</PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Start Time</PanelRowDescriptor>
              <PanelRowDisplay>
                {moment.unix(control.start).format(DATE_FORMAT)}
              </PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Duration</PanelRowDescriptor>
              <PanelRowDisplay>
                {durationFormatter(parseInt(control.duration) * 100)}
              </PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>End Time</PanelRowDescriptor>
              <PanelRowDisplay>
                {moment.unix(endTime).format(DATE_FORMAT)}
              </PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Associated Node</PanelRowDescriptor>
              <AssociatedNodeRow>
                {control.nodeId}
                <ExternalIcon
                  src={externalLink}
                  alt={`${control.nodeId} information`}
                  onClick={onAssociatedNode}
                />
              </AssociatedNodeRow>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Control Id</PanelRowDescriptor>
              <PanelRowDisplay>{control.controlId}</PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Device Category</PanelRowDescriptor>
              <PanelRowDisplay>{deviceCategories()}</PanelRowDisplay>
            </SubPanelRow>
            {externallyManagedEdevs && (
              <SubPanelRow>
                <PanelRowDescriptor>Remotely Managed</PanelRowDescriptor>
                <PanelRowDisplay>
                  {isRemoteResource ? 'True' : 'False'}
                </PanelRowDisplay>
              </SubPanelRow>
            )}
          </SubPanel>

          <Break margin="16px 0" />

          <SubPanel>
            <SectionHeader header="Control Mode(s)" />
            <ControlFieldsContainer
              controlTypes={controlTypes}
              curves={curves}
            />
          </SubPanel>

          <Break margin="16px 0" />

          <SubPanel>
            <SectionHeader header="Responses" />
            <Table
              smallTitle="Devices Received"
              rowData={devicesReceived}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Started"
              rowData={devicesStarted}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Completed"
              rowData={devicesCompleted}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Opted Out"
              rowData={devicesOptOut}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Opted In"
              rowData={devicesOptIn}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Cancelled"
              rowData={devicesCancelled}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Superseded"
              rowData={devicesSuperseded}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Partially Completed"
              rowData={devicesPartiallyCompleted}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
            <Table
              smallTitle="Devices Aborted"
              rowData={devicesAborted}
              columnDefs={deviceColumns}
              height="200px"
              padding="0px"
              margin="20px 0px 30px 0px"
              onRowClicked={onDeviceSelected}
            />
          </SubPanel>
        </PanelContent>
        <PanelButtonRow>
          <WhiteButton
            onClick={() =>
              determineBack(
                sidePanel,
                () => dispatch(revertSidePanel()),
                () => dispatch(closeSidePanel())
              )
            }
          >
            Back
          </WhiteButton>
          {showControlCancelButton && (
            <ButtonRow>
              <TooltipRow
                disabled={isRemoteResource}
                message={disabledRemoteMessage}
              >
                <CancelButton
                  onClick={() =>
                    dispatch(
                      setSidePanel({
                        entity: RESOURCE_TYPES.CONTROL,
                        state: SIDEPANEL_STATES.DELETE,
                        keepBreadcrumbs: true,
                        id: control.controlId
                      })
                    )
                  }
                  disabled={isRemoteResource}
                >
                  Cancel Control
                </CancelButton>
              </TooltipRow>
            </ButtonRow>
          )}
        </PanelButtonRow>
      </FullPanel>
    );
  }
};

export default ControlPanel;
